import { useState, useEffect } from 'react';
import { collection, query, getDocs, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

const commonOptions = {
  responsive: true,
  maintainAspectRatio: false,
  color: 'rgba(255, 255, 255, 0.5)',
  plugins: {
    legend: {
      labels: { color: 'rgba(255, 255, 255, 0.6)', font: { family: 'Inter', size: 12 } }
    },
    tooltip: {
      backgroundColor: 'rgba(14, 20, 38, 0.95)',
      titleColor: '#fff',
      bodyColor: '#fff',
      borderColor: 'rgba(255,255,255,0.08)',
      borderWidth: 1,
      padding: 12,
      cornerRadius: 10,
      titleFont: { family: 'Outfit', size: 13 },
      bodyFont: { family: 'Inter', size: 12 }
    }
  },
  scales: {
    x: {
      grid: { color: 'rgba(255, 255, 255, 0.03)' },
      ticks: { color: 'rgba(255, 255, 255, 0.4)', font: { family: 'Inter', size: 11 } }
    },
    y: {
      grid: { color: 'rgba(255, 255, 255, 0.03)' },
      ticks: { color: 'rgba(255, 255, 255, 0.4)', font: { family: 'Inter', size: 11 } }
    }
  }
};

function Analytics() {
  const [loading, setLoading] = useState(true);
  const [realData, setRealData] = useState({
    tripsByDay: [],
    revenueByDay: [],
    statusCounts: { completed: 0, cancelled: 0, noDrivers: 0 },
    totalTrips: 0,
    totalRevenue: 0,
    completionRate: 0,
  });

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        // Fetch last 100 trips to compute analytics
        const q = query(collection(db, 'trips'), orderBy('createdAt', 'desc'), limit(100));
        let snapshot;
        try {
          snapshot = await getDocs(q);
        } catch {
          // Fallback without orderBy if index is missing
          const q2 = query(collection(db, 'trips'), limit(100));
          snapshot = await getDocs(q2);
        }

        const trips = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        
        // Compute status counts
        let completed = 0, cancelled = 0, noDrivers = 0, revenue = 0;
        const dayMap = {};
        const revMap = {};
        
        trips.forEach(t => {
          if (t.status === 'completed') { completed++; revenue += (t.fareActual || t.agreedFare || 0); }
          else if (t.status === 'cancelled') cancelled++;
          else if (t.status === 'no_drivers') noDrivers++;

          // Group by day
          const ts = t.createdAt;
          if (ts?.toDate) {
            const d = ts.toDate();
            const key = d.toLocaleDateString('es-VE', { weekday: 'short' });
            dayMap[key] = (dayMap[key] || 0) + 1;
            if (t.status === 'completed') {
              revMap[key] = (revMap[key] || 0) + (t.fareActual || t.agreedFare || 0);
            }
          }
        });

        const total = completed + cancelled + noDrivers;
        setRealData({
          tripsByDay: dayMap,
          revenueByDay: revMap,
          statusCounts: { completed, cancelled, noDrivers },
          totalTrips: trips.length,
          totalRevenue: revenue,
          completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
        });
      } catch (error) {
        console.error("Error fetching analytics:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  const dayLabels = Object.keys(realData.tripsByDay).length > 0 
    ? Object.keys(realData.tripsByDay) 
    : ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

  const lineData = {
    labels: dayLabels,
    datasets: [{
      label: 'Viajes',
      data: dayLabels.map(k => realData.tripsByDay[k] || 0),
      borderColor: '#6366f1',
      backgroundColor: 'rgba(99, 102, 241, 0.08)',
      borderWidth: 2,
      fill: true,
      tension: 0.4,
      pointBackgroundColor: '#6366f1',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 4,
    }]
  };

  const barData = {
    labels: dayLabels,
    datasets: [{
      label: 'Ingresos (USD)',
      data: dayLabels.map(k => realData.revenueByDay[k] || 0),
      backgroundColor: 'rgba(16, 185, 129, 0.7)',
      borderRadius: 6,
      borderSkipped: false,
    }]
  };

  const doughnutData = {
    labels: ['Completados', 'Cancelados', 'Sin Conductores'],
    datasets: [{
      data: [
        realData.statusCounts.completed || 1, 
        realData.statusCounts.cancelled || 0, 
        realData.statusCounts.noDrivers || 0
      ],
      backgroundColor: ['#10b981', '#ef4444', '#f59e0b'],
      borderWidth: 0,
      hoverOffset: 6
    }]
  };

  if (loading) {
    return (
      <div className="charts-grid">
        {[1,2,3].map(i => <div key={i} className="skeleton-card" style={{height:'320px'}} />)}
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div>
          <h2 className="page-title">Analíticas</h2>
          <p className="page-subtitle">Métricas de rendimiento basadas en los últimos 100 viajes</p>
        </div>
      </div>

      <div className="charts-grid">
        <div className="chart-card" style={{height:'360px'}}>
          <h3 className="chart-title">Distribución de Viajes</h3>
          <div className="chart-area">
            <Line options={commonOptions} data={lineData} />
          </div>
        </div>

        <div className="chart-card" style={{height:'360px'}}>
          <h3 className="chart-title">Ingresos Brutos (USD)</h3>
          <div className="chart-area">
            <Bar options={commonOptions} data={barData} />
          </div>
        </div>

        <div className="chart-card chart-card-full" style={{minHeight:'340px'}}>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'2rem', height:'100%'}}>
            <div style={{display:'flex', flexDirection:'column'}}>
              <h3 className="chart-title">Estado de Viajes</h3>
              <div className="chart-area" style={{display:'flex', alignItems:'center', justifyContent:'center'}}>
                <div style={{width:'100%', maxWidth:'220px', maxHeight:'220px'}}>
                  <Doughnut 
                    data={doughnutData} 
                    options={{
                      ...commonOptions,
                      scales: { x: { display: false }, y: { display: false } },
                      cutout: '68%'
                    }} 
                  />
                </div>
              </div>
            </div>
            
            <div style={{display:'flex', flexDirection:'column', justifyContent:'center'}}>
              <h3 className="chart-title">Resumen General</h3>
              <div style={{display:'flex', flexDirection:'column', gap:'0.75rem'}}>
                <div className="metric-row">
                  <span className="metric-label">Tasa de Completación</span>
                  <span className="metric-value" style={{color:'var(--accent-green)'}}>{realData.completionRate}%</span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">Total de Viajes Analizados</span>
                  <span className="metric-value">{realData.totalTrips}</span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">Ingreso Total Acumulado</span>
                  <span className="metric-value" style={{color:'var(--accent-orange)'}}>${realData.totalRevenue.toFixed(2)}</span>
                </div>
                <div className="metric-row">
                  <span className="metric-label">Promedio por Viaje Completado</span>
                  <span className="metric-value" style={{color:'var(--accent-cyan)'}}>
                    ${realData.statusCounts.completed > 0 
                      ? (realData.totalRevenue / realData.statusCounts.completed).toFixed(2) 
                      : '0.00'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Analytics;
